import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Box, Link, Tooltip, Typography } from '@mui/material';
import type { DriverType } from '@repo/supergraph/marketplace/types';
import { DisclosureTooltip } from '@repo/ui/DisclosureTooltip';

type DriverContentVariant = 'grid' | 'stacked';

type DriverContentProps = {
  driver: DriverType;
  variant: DriverContentVariant;
  showDescription?: boolean;
};

export function DriverContent({ driver, variant, showDescription = true }: DriverContentProps) {
  const isGrid = variant === 'grid';
  const valueText = driver.value || 'N/A';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: isGrid ? 'center' : { xs: 'center', md: 'flex-start' },
        textAlign: isGrid ? 'center' : { xs: 'center', md: 'left' },
        gap: 1,
      }}
      data-testid="card-driver-component"
    >
      {driver.label &&
        (driver.labelTooltip ? (
          <Tooltip title={driver.labelTooltip}>
            <Typography
              variant={isGrid ? 'textSmallBold' : 'textBold'}
              sx={
                isGrid
                  ? {
                      color: 'black',
                      textTransform: 'uppercase',
                      fontSize: '11px',
                    }
                  : { color: 'neutral.darker' }
              }
            >
              {driver.label}
            </Typography>
          </Tooltip>
        ) : (
          <Typography
            variant={isGrid ? 'textSmallBold' : 'textBold'}
            sx={
              isGrid
                ? {
                    color: 'black',
                    textTransform: 'uppercase',
                    fontSize: '11px',
                  }
                : { color: 'neutral.darker' }
            }
          >
            {driver.label}
          </Typography>
        ))}

      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Typography
          variant="text"
          sx={{
            color: 'black',
            fontVariantNumeric: 'tabular-nums',
            ...(isGrid ? { fontSize: '14px' } : {}),
          }}
        >
          {valueText}
        </Typography>

        {driver.valueTooltip && (
          <DisclosureTooltip
            tooltipStyleOverride={
              isGrid ? { maxWidth: '275px', paddingX: 3, paddingY: 2 } : undefined
            }
            trigger={
              <Link aria-label="driver info" component="button" lineHeight={1}>
                <HelpOutlineIcon
                  sx={{
                    width: isGrid ? '16px' : '14px',
                    height: isGrid ? '16px' : '14px',
                    color: 'neutral.dark',
                  }}
                />
              </Link>
            }
          >
            <Typography
              variant={isGrid ? 'textSmall' : undefined}
              sx={!isGrid ? { fontSize: '0.813rem' } : undefined}
            >
              {driver.valueTooltip}
            </Typography>
          </DisclosureTooltip>
        )}
      </Box>

      {showDescription && driver.description && (
        <Typography variant="textSmall" sx={{ color: 'black' }}>
          {driver.description}
        </Typography>
      )}
    </Box>
  );
}
